import { useEffect, useRef, RefObject } from 'react';

// Inspired: https://usehooks-ts.com/react-hook/use-event-listener
function useEventListener<K extends keyof WindowEventMap>(
    eventName: K,
    handler: (e: WindowEventMap[K]) => void,
    element?: RefObject<HTMLElement>,
    handlerOptions?: AddEventListenerOptions
): void {
    // Create a ref that stores handler
    const savedHandler = useRef(handler);

    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);

    useEffect(() => {
        // Define the listening target
        const targetElement: HTMLElement | Window = element?.current || window;

        if (!targetElement?.addEventListener) {
            return;
        }

        // Create event listener that calls handler function stored in ref
        const eventListener: EventListenerOrEventListenerObject = (event) =>
            savedHandler.current(event as WindowEventMap[K]);

        targetElement.addEventListener(eventName, eventListener, handlerOptions);

        // Remove event listener on cleanup
        return () => {
            targetElement.removeEventListener(eventName, eventListener);
        };
    }, [eventName, element, handlerOptions]);
}

export default useEventListener;
